import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[8px] text-[16px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-[#2563EB] text-white hover:bg-[#3B82F6] active:bg-[#1E40AF]",
        secondary:
          "bg-white text-[#2563EB] border border-[#2563EB] hover:bg-[#DBEAFE]",
        text: "text-[#2563EB] hover:bg-[#DBEAFE]",
        ghost: "hover:bg-[#F3F4F6] text-[#111827]",
        destructive:
          "bg-[#EF4444] text-white hover:bg-[#DC2626]",
      },
      size: {
        default: "h-12 px-4 min-w-[120px]",
        sm: "h-10 px-3",
        lg: "h-14 px-6",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };

